import React from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { FuturePurchaseFormValues } from "src/models/FuturePurchase.model";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import moment from "moment";
import { GrFormAdd } from "react-icons/gr";
import { RiDeleteBin5Line } from "react-icons/ri";
import { getInputHeight } from "src/utils/formUtils/getInputHeight";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import useSubjects from "src/hooks/useSubject";
import useFaculty from "src/hooks/useFaculty";

type Props = {
  formikProps: FormikProps<FuturePurchaseFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const FuturePurchaseForm = ({ formikProps, onClose, formType }: Props) => {
  const { subjects, isDataLoading: issubjectsDataLoading } = useSubjects();
  const { faculty, isDataLoading } = useFaculty();

  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const getSelectedsubjectsIds = (currentIndex: number) => {
    return values.subjects
      .filter((_, idx) => idx !== currentIndex)
      .map((subjects) => subjects?.value);
  };
  const getsubjectsOptions = (currentIndex: number) => {
    const selectedIds = getSelectedsubjectsIds(currentIndex);
    return (
      subjects
        ?.filter((subjects) => !selectedIds.includes(subjects._id))
        .map((subjects) => ({
          label: subjects?.subjectName,
          value: subjects?._id,
        })) || []
    );
  };
  const getSelectedFacultyIds = (currentIndex: number) => {
    return values.faculties
      .filter((_, idx) => idx !== currentIndex)
      .map((faculties) => faculties?.value);
  };
  const getFacultyOptions = (currentIndex: number) => {
    const selectedIds = getSelectedFacultyIds(currentIndex);
    return (
      faculty
        ?.filter((faculties) => !selectedIds.includes(faculties._id))
        .map((faculties) => ({
          label: faculties?.facultyName,
          value: faculties?._id,
        })) || []
    );
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add FuturePurchase" : "Update FuturePurchase"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        <div>
          <ATMTextField
            name="name"
            value={values.name}
            required
            onChange={(e) => {
              setFieldValue("name", e.target.value);
            }}
            label="Future Purchaser Name"
            onBlur={handleBlur}
          />
        </div>

        <div>
          <ATMTextField
            name="mobile"
            value={values.mobile}
            required
            onChange={(e) => {
              setFieldValue("mobile", e.target.value);
            }}
            label="Mobile No."
            onBlur={handleBlur}
          />
        </div>
        <div>
          <ATMDatePicker
            label=" Date of Purchase"
            name="purchaseDate"
            format="DD/MM/YYYY"
            minDate={moment().format("yyyy-MM-DD")}
            value={values.purchaseDate || null}
            onChange={(newValue) =>
              setFieldValue(
                "purchaseDate",
                moment(newValue)?.format("yyyy-MM-DD")
              )
            }
          />
        </div>
        <div className="my-6">
          <p className="border-l-2 px-2 mt-3 border-black font-medium">
            subjects
          </p>
          {/* Label Name */}
          <FieldArray name="subjects">
            {({ push, remove }) => (
              <div className="grid grid-cols-2 gap-3 ">
                {values?.subjects?.map((subjects: any, index: any) => (
                  <div key={index} className="flex gap-4 items-end">
                    {/* subjects Name*/}
                    <div className="">
                      <div>
                        <ATMSelect
                          isLoading={issubjectsDataLoading}
                          name={`subjects[${index}]`}
                          required
                          placeholder="select subjects"
                          label="subjects"
                          options={getsubjectsOptions(index)}
                          onChange={(newValue: any) => {
                            setFieldValue(`subjects[${index}]`, newValue);
                          }}
                          value={values.subjects[index]}
                        />
                      </div>
                    </div>

                    {/* Delete Button */}
                    <button
                      type="button"
                      className={`${getInputHeight(
                        "small"
                      )} aspect-square rounded bg-red-400 text-white flex justify-center items-center `}
                      onClick={() => remove(index)}
                    >
                      <RiDeleteBin5Line size={20} />
                    </button>
                  </div>
                ))}

                {/* Add More Button */}
                <button
                  type="button"
                  onClick={() => push({})}
                  className="rounded border-dashed ml-38 mt-2 border border-slate-500 py-2 font-medium text-md text-gray-400 flex justify-center items-center gap-[2px]"
                >
                  Add {values?.subjects?.length ? "More" : "subjects Name"}{" "}
                  <span>
                    <GrFormAdd
                      color="#ADB8C8"
                      size="1.5em"
                      className="font-semibold"
                    />
                  </span>
                </button>
              </div>
            )}
          </FieldArray>
        </div>
        <div className="my-6">
          <p className="border-l-2 px-2 mt-3 border-black font-medium">
            faculties
          </p>
          {/* Label Name */}
          <FieldArray name="faculties">
            {({ push, remove }) => (
              <div className="grid grid-cols-2 gap-3 ">
                {values?.faculties?.map((faculties: any, index: any) => (
                  <div key={index} className="flex gap-4 items-end">
                    {/* faculties Name*/}
                    <div className="">
                      <div>
                        <ATMSelect
                          isLoading={isDataLoading}
                          name={`faculties[${index}]`}
                          required
                          placeholder="select faculties"
                          label="faculties"
                          options={getFacultyOptions(index)}
                          onChange={(newValue: any) => {
                            setFieldValue(`faculties[${index}]`, newValue);
                          }}
                          value={values.faculties[index]}
                        />
                      </div>
                    </div>

                    {/* Delete Button */}
                    <button
                      type="button"
                      className={`${getInputHeight(
                        "small"
                      )} aspect-square rounded bg-red-400 text-white flex justify-center items-center `}
                      onClick={() => remove(index)}
                    >
                      <RiDeleteBin5Line size={20} />
                    </button>
                  </div>
                ))}

                {/* Add More Button */}
                <button
                  type="button"
                  onClick={() => push({})}
                  className="rounded border-dashed ml-38 mt-2 border border-slate-500 py-2 font-medium text-md text-gray-400 flex justify-center items-center gap-[2px]"
                >
                  Add {values?.faculties?.length ? "More" : "faculties Name"}{" "}
                  <span>
                    <GrFormAdd
                      color="#ADB8C8"
                      size="1.5em"
                      className="font-semibold"
                    />
                  </span>
                </button>
              </div>
            )}
          </FieldArray>
        </div>
      </div>
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default FuturePurchaseForm;
